<template>
  <div id="app">
    <AppHeader :minimal="isLandingPage" :room="isRoomPage" />

    <div v-if="subscriptionMessage" class="subscription-toast">
      {{ subscriptionMessage }}
      <button @click="subscriptionMessage = null" class="toast-close">&times;</button>
    </div>

    <div class="app-content" :class="{ 'app-content--landing': isLandingPage, 'app-content--room': isRoomPage }">
      <div class="container" :class="{ 'container--landing': isLandingPage, 'container--room': isRoomPage }">
        <router-view></router-view>
      </div>
    </div>

    <!-- Dev-only premium toggle -->
    <button v-if="showDevToggle" type="button" class="dev-toggle" @click="togglePremium">
      <span class="dev-toggle-label">{{ isPremium ? 'PRO' : 'FREE' }}</span>
    </button>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import AppHeader from './components/AppHeader.vue'
import { useAuth } from './composables/useAuth'
import { useSubscription } from './composables/useSubscription'

export default {
  name: 'App',
  components: {
    AppHeader
  },
  setup() {
    const route = useRoute()
    const isLandingPage = computed(() => route.name === 'LandingPage')
    const isRoomPage = computed(() => route.name === 'Room')
    const useEmulator =
      process.env.NODE_ENV === 'development' &&
      process.env.VUE_APP_USE_FIREBASE_EMULATOR !== 'false'
    const { user } = useAuth()
    const { isPremium } = useSubscription()
    const subscriptionMessage = ref(null)

    onMounted(() => {
      const params = new URLSearchParams(window.location.search)
      const status = params.get('subscription')
      if (status === 'success') {
        subscriptionMessage.value = 'Welcome to Premium! Your subscription is now active.'
        // Clean query param from URL
        window.history.replaceState({}, '', window.location.pathname)
        setTimeout(() => { subscriptionMessage.value = null }, 5000)
      } else if (status === 'canceled') {
        subscriptionMessage.value = 'Subscription checkout was canceled.'
        window.history.replaceState({}, '', window.location.pathname)
        setTimeout(() => { subscriptionMessage.value = null }, 4000)
      }
    })

    return { showDevToggle: useEmulator, user, isPremium, subscriptionMessage, isLandingPage, isRoomPage }
  },
  methods: {
    togglePremium() {
      const newValue = !this.isPremium
      try {
        localStorage.setItem('dev_isPremium', String(newValue))
      } catch (_) {
        // Ignore localStorage errors (e.g. disabled in browser settings)
      }
      // Force the app to re-read the dev override from localStorage.
      location.reload()
    }
  }
}
</script>

<style>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
}

.app-content {
  padding-top: 60px; /* Height of the header */
}

.app-content--landing {
  padding-top: 0;
}

.app-content--room {
  padding-top: 60px;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.container--landing {
  max-width: none;
  margin: 0;
  padding: 0;
}

.container--room {
  max-width: none;
  margin: 0;
  padding: 0;
}

.dev-toggle {
  position: fixed;
  bottom: 12px;
  left: 12px;
  appearance: none;
  background: #1a1a1a;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 4px 10px;
  cursor: pointer;
  z-index: 99999;
  font-size: 0.7rem;
  font-weight: 700;
  font-family: monospace;
  user-select: none;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.dev-toggle:hover {
  opacity: 1;
}

.dev-toggle-label {
  color: #ffa500;
}

.subscription-toast {
  position: fixed;
  top: 70px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(26, 26, 26, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 165, 0, 0.3);
  border-radius: 8px;
  padding: 0.75rem 1.25rem;
  color: #ffa500;
  font-size: 0.9rem;
  font-weight: 500;
  z-index: 10000;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from { opacity: 0; transform: translateX(-50%) translateY(-10px); }
  to { opacity: 1; transform: translateX(-50%) translateY(0); }
}

.toast-close {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  font-size: 1.1rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.toast-close:hover {
  color: white;
}
</style>
